/**
 * Hook for managing messages - Messaging System.
 */

import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../auth/useAuth';
import * as messagesApi from '../api/messages';
import type { Message, MessageThread, SendMessageRequest } from '../types/message';

interface UseMessagesResult {
  threads: MessageThread[];
  messages: Message[];
  currentThreadId: string | null;
  unreadCount: number;
  isLoading: boolean;
  error: string | null;
  hasMoreThreads: boolean;
  hasMoreMessages: boolean;
  loadMoreThreads: () => Promise<void>;
  loadMoreMessages: () => Promise<void>;
  sendMessage: (request: SendMessageRequest) => Promise<Message>;
  selectThread: (threadId: string | null) => Promise<void>;
  deleteThread: (threadId: string) => Promise<void>;
  markThreadAsRead: (threadId: string) => Promise<void>;
  refresh: () => Promise<void>;
}

const POLLING_INTERVAL = 30000; // 30 seconds
const THREADS_PAGE_SIZE = 20;
const MESSAGES_PAGE_SIZE = 50;

/**
 * Hook for fetching and managing user messages.
 * Includes polling for unread count and pagination support.
 */
export const useMessages = (): UseMessagesResult => {
  const { isAuthenticated } = useAuth();
  const [threads, setThreads] = useState<MessageThread[]>([]);
  const [messages, setMessages] = useState<Message[]>([]);
  const [currentThreadId, setCurrentThreadId] = useState<string | null>(null);
  const [unreadCount, setUnreadCount] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [threadsPage, setThreadsPage] = useState<number>(0);
  const [messagesPage, setMessagesPage] = useState<number>(0);
  const [hasMoreThreads, setHasMoreThreads] = useState<boolean>(true);
  const [hasMoreMessages, setHasMoreMessages] = useState<boolean>(true);

  /**
   * Fetch unread count from the API.
   */
  const fetchUnreadCount = useCallback(async () => {
    if (!isAuthenticated) return;
    
    try {
      const count = await messagesApi.getUnreadCount();
      setUnreadCount(count);
    } catch (err: any) {
      // Only log non-network errors (network errors are expected when backend is down)
      if (err?.response) {
        console.error('Error fetching unread message count:', err);
      }
      // Network errors are handled silently - count will remain at 0
    }
  }, [isAuthenticated]);

  /**
   * Fetch threads from the API.
   */
  const fetchThreads = useCallback(async (pageNum: number = 0, append: boolean = false) => {
    if (!isAuthenticated) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      const response = await messagesApi.getThreads(pageNum, THREADS_PAGE_SIZE);
      
      if (append) {
        setThreads(prev => [...prev, ...response.content]);
      } else {
        setThreads(response.content);
      }
      
      setHasMoreThreads(!response.last);
      setThreadsPage(pageNum);
    } catch (err: any) {
      // Only log/show error for actual server errors, not network errors
      if (err?.response) {
        console.error('Error fetching threads:', err);
        setError('Failed to load message threads');
      }
      // Network errors (backend down) are handled silently
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated]);

  /**
   * Fetch messages for a thread.
   */
  const fetchMessages = useCallback(async (threadId: string, pageNum: number = 0, append: boolean = false) => {
    if (!isAuthenticated) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      const response = await messagesApi.getMessages(threadId, pageNum, MESSAGES_PAGE_SIZE);
      
      if (append) {
        // API returns messages DESC (newest first), but we want chronological order (oldest first)
        // When appending older messages, reverse them and prepend to existing messages
        const reversedNewMessages = [...response.content].reverse();
        setMessages(prev => [...reversedNewMessages, ...prev]);
      } else {
        // Reverse to show oldest first (chronological order, top to bottom)
        setMessages([...response.content].reverse());
      }
      
      setHasMoreMessages(!response.last);
      setMessagesPage(pageNum);
    } catch (err: any) {
      // Only log/show error for actual server errors, not network errors
      if (err?.response) {
        console.error('Error fetching messages:', err);
        setError('Failed to load messages');
      }
      // Network errors (backend down) are handled silently
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated]);

  /**
   * Load more threads (pagination).
   */
  const loadMoreThreads = useCallback(async () => {
    if (isLoading || !hasMoreThreads) return;
    await fetchThreads(threadsPage + 1, true);
  }, [fetchThreads, isLoading, hasMoreThreads, threadsPage]);

  /**
   * Load more messages (pagination).
   */
  const loadMoreMessages = useCallback(async () => {
    if (isLoading || !hasMoreMessages || !currentThreadId) return;
    await fetchMessages(currentThreadId, messagesPage + 1, true);
  }, [fetchMessages, isLoading, hasMoreMessages, currentThreadId, messagesPage]);

  /**
   * Send a message.
   */
  const sendMessage = useCallback(async (request: SendMessageRequest): Promise<Message> => {
    try {
      const newMessage = await messagesApi.sendMessage(request);
      
      // If this is the current thread, add the message to the end of the list (newest at bottom)
      // The backend will have created/updated the thread
      if (currentThreadId === newMessage.threadId) {
        setMessages(prev => [...prev, newMessage]);
      }
      
      // Refresh threads to update lastMessageAt and unread count
      await fetchThreads(0, false);
      await fetchUnreadCount();
      
      return newMessage;
    } catch (err: any) {
      // Only log actual server errors, not network errors
      if (err?.response) {
        console.error('Error sending message:', err);
      }
      throw err; // Re-throw so caller can handle
    }
  }, [currentThreadId, fetchThreads, fetchUnreadCount]);

  /**
   * Select a thread and load its messages.
   */
  const selectThread = useCallback(async (threadId: string | null) => {
    if (threadId === null) {
      setCurrentThreadId(null);
      setMessages([]);
      setMessagesPage(0);
      setHasMoreMessages(true);
      return;
    }

    setCurrentThreadId(threadId);
    setMessagesPage(0);
    setHasMoreMessages(true);
    await fetchMessages(threadId, 0, false);

    // Mark thread as read when viewing
    try {
      await messagesApi.markThreadAsRead(threadId);
      await fetchUnreadCount();

      // Update thread in list to reflect read status
      setThreads(prev =>
        prev.map(t => (t.id === threadId ? { ...t, unreadCount: 0 } : t))
      );
    } catch (err: any) {
      // Only log actual server errors, not network errors
      if (err?.response) {
        console.error('Error marking thread as read:', err);
      }
      // Network errors are handled silently
    }
  }, [fetchMessages, fetchUnreadCount]);

  /**
   * Mark a thread as read.
   */
  const markThreadAsRead = useCallback(async (threadId: string) => {
    try {
      await messagesApi.markThreadAsRead(threadId);
      await fetchUnreadCount();
      
      // Update thread in list
      setThreads(prev =>
        prev.map(t => t.id === threadId ? { ...t, unreadCount: 0 } : t)
      );
    } catch (err: any) {
      // Only log actual server errors, not network errors
      if (err?.response) {
        console.error('Error marking thread as read:', err);
      }
      throw err; // Re-throw so caller can handle
    }
  }, [fetchUnreadCount]);

  /**
   * Delete a thread and all its messages.
   */
  const deleteThread = useCallback(async (threadId: string) => {
    try {
      await messagesApi.deleteThread(threadId);

      setThreads(prev => prev.filter(t => t.id !== threadId));

      if (currentThreadId === threadId) {
        setCurrentThreadId(null);
        setMessages([]);
        setMessagesPage(0);
        setHasMoreMessages(true);
      }

      await fetchUnreadCount();
    } catch (err: any) {
      if (err?.response) {
        console.error('Error deleting thread:', err);
      }
      throw err;
    }
  }, [currentThreadId, fetchUnreadCount]);

  /**
   * Refresh threads and unread count.
   */
  const refresh = useCallback(async () => {
    await fetchThreads(0, false);
    await fetchUnreadCount();
  }, [fetchThreads, fetchUnreadCount]);

  // Initial fetch when authenticated
  useEffect(() => {
    if (isAuthenticated) {
      fetchUnreadCount();
      fetchThreads(0, false);
    } else {
      setUnreadCount(0);
      setThreads([]);
      setMessages([]);
      setCurrentThreadId(null);
    }
  }, [isAuthenticated, fetchUnreadCount, fetchThreads]);

  // Poll unread count
  useEffect(() => {
    if (!isAuthenticated) return;
    const id = setInterval(fetchUnreadCount, POLLING_INTERVAL);
    return () => clearInterval(id);
  }, [isAuthenticated, fetchUnreadCount]);

  // Poll for new messages in the active thread every 30 seconds.
  // Only adds messages we don't already have (merge by id).
  useEffect(() => {
    if (!isAuthenticated || !currentThreadId) return;

    const threadId = currentThreadId; // capture for closure

    const pollMessages = async () => {
      try {
        const response = await messagesApi.getMessages(threadId, 0, MESSAGES_PAGE_SIZE);
        const incoming = [...response.content].reverse(); // oldest first

        setMessages(prev => {
          const existingIds = new Set(prev.map(m => m.id));
          const newOnes = incoming.filter(m => !existingIds.has(m.id));
          if (newOnes.length === 0) return prev;

          // Also silently refresh thread list so sidebar preview stays current
          fetchThreads(0, false).catch(() => {});
          return [...prev, ...newOnes];
        });
      } catch {
        // silent – polling is best-effort
      }
    };

    const id = setInterval(pollMessages, POLLING_INTERVAL);
    return () => clearInterval(id);
  }, [isAuthenticated, currentThreadId, fetchThreads]);

  return {
    threads,
    messages,
    currentThreadId,
    unreadCount,
    isLoading,
    error,
    hasMoreThreads,
    hasMoreMessages,
    loadMoreThreads,
    loadMoreMessages,
    sendMessage,
    selectThread,
    deleteThread,
    markThreadAsRead,
    refresh,
  };
};

export default useMessages;
